/* eslint-disable ember/no-get, ember/no-new-mixins, warp-drive/no-legacy-request-patterns */
import Mixin from '@ember/object/mixin';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AssessmentType from '../enums/assessment-type';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default Mixin.create({
  // Services
  session: service(),
  store: service(),

  editingAssessment: null,
  assessmentLabel: '',
  assessmentDescription: '',
  assessmentCategory: null,
  assessmentType: null,
  assessmentIsModerated: false,
  assessmentGradeSchedule: null,
  isVisibleToLearners: false,
  showHistoryToLearners: false,
  canLearnersEdit: false,
  assessmentDisplay: null,
  noGradesColumnsToShowCaregiversStudents: 4,
  noGradesColumnsToShowStaff: 8,
  showToCaregiversOnlyIfContent: false,

  toggleAssessmentDialog: action(function () {
    this.toggleProperty('assessmentDialogOpen');
  }),

  editAssessment: action(async function (assessment) {
    this.resetAssessmentForm();
    this.set('editingAssessment', assessment);
    this.set('assessmentLabel', assessment.get('label'));
    this.set('assessmentDescription', assessment.get('description'));
    this.set(
      'assessmentType',
      findBy(AssessmentType.values, 'value', assessment.get('type')),
    );
    this.set('assessmentDisplay', assessment.get('assessmentDisplay'));
    this.set('assessmentIsModerated', !!assessment.get('isModerated'));
    // A belongsTo takes a record, not the async proxy; the picker and
    // saveAssessment need the record.
    this.set('assessmentGradeSchedule', await assessment.get('gradeSchedule'));
    this.set('assessmentDialogOpen', true);
    this.set('isVisibleToLearners', assessment.get('isVisibleToLearners'));
    if (assessment.get('noGradesColumnsToShowCaregiversStudents')) {
      this.set(
        'noGradesColumnsToShowCaregiversStudents',
        assessment.get('noGradesColumnsToShowCaregiversStudents'),
      );
    }
    if (assessment.get('noGradesColumnsToShowStaff')) {
      this.set(
        'noGradesColumnsToShowStaff',
        assessment.get('noGradesColumnsToShowStaff'),
      );
    }
    if (!isEmpty(assessment.get('showToCaregiversOnlyIfContent'))) {
      this.set(
        'showToCaregiversOnlyIfContent',
        assessment.get('showToCaregiversOnlyIfContent'),
      );
    }
    this.set('showHistoryToLearners', assessment.get('showHistoryToLearners'));
    this.set('canLearnersEdit', assessment.get('canLearnersEdit'));
    if (!assessment.get('assessmentDisplay')) {
      this.set('assessmentDisplay', AssessmentDisplay.TERMS);
    }
  }),

  saveAssessment: action(function () {
    let assessment = this.editingAssessment;
    let store = this.store;
    this.set('assessmentSaveInProgress', true);

    // Create a new Assessment if needed
    if (isEmpty(assessment)) {
      assessment = store.createRecord('assessments/assessment', {
        schoolId: this.get('session.school'),
        categoryId: this.assessmentCategory,
        authorId: this.get('session.person'),
        // Add to the end by default
        sequence: this.get('assessmentCategory.assessments.length'),
      });
    }

    // Set properties from form
    assessment.setProperties({
      label: this.assessmentLabel,
      description: this.assessmentDescription,
      type: this.get('assessmentType.value'),
      assessmentDisplay: this.assessmentDisplay,
      isModerated: this.assessmentIsModerated,
      gradeScheduleId: this.assessmentGradeSchedule,
      isVisibleToLearners: this.isVisibleToLearners,
      showHistoryToLearners: this.showHistoryToLearners,
      canLearnersEdit: this.canLearnersEdit,
      showToCaregiversOnlyIfContent: this.showToCaregiversOnlyIfContent,
      noGradesColumnsToShowCaregiversStudents:
        this.noGradesColumnsToShowCaregiversStudents,
      noGradesColumnsToShowStaff: this.noGradesColumnsToShowStaff,
    });

    assessment
      .save()
      .then(() => {
        this.set('assessmentDialogOpen', false);
      })
      .catch((err) => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(assessment);
        this.set('assessmentErrorMessage', err);
      })
      .then(() => {
        this.set('assessmentSaveInProgress', false);
      });
  }),

  resetAssessmentForm() {
    this.set('assessmentErrorMessage', '');
    this.set('assessmentLabel', '');
    this.set('assessmentDescription', '');
    this.set('assessmentCategory', null);
    this.set('assessmentType', null);
    this.set('assessmentIsModerated', false);
    this.set('assessmentGradeSchedule', null);
    this.set('editingAssessment', null);
    this.set('isVisibleToLearners', false);
    this.set('showHistoryToLearners', false);
    this.set('canLearnersEdit', false);
    this.set('showToCaregiversOnlyIfContent', false);
    this.set('noGradesColumnsToShowCaregiversStudents', 4);
    this.set('noGradesColumnsToShowStaff', 8);
  },
});
